import React, { useState, useRef, useEffect } from 'react';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import logo from 'assets/img/logo-full-blue.webp';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import {
  DocumentTemplate,
  DocumentTemplateSubmission,
  Physician,
  Patient,
  SubmitExternalReferralRequest,
} from 'generated/api';
import DocumentTemplateComponent from 'components/HealthRecords/Documents/DocumentTemplate';
import MiscUtils from 'services/MiscUtils';
import ValidatorUtils from 'services/ValidatorUtils';
import moment from 'moment-timezone';

interface PatientInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  ohip_number: string;
}

interface FilledTemplate {
  template_id: string;
  template_name: string;
  html: string;
  metadata: string;
}

interface Attachment {
  id: string;
  type: 'file' | 'template';
  name: string;
  size?: number;
  file?: File;
  template?: FilledTemplate;
}

type DocumentTemplateController = {
  getTemplate?: () => Promise<DocumentTemplateSubmission>;
};

// Helper function to generate unique attachment IDs
const generateAttachmentId = (prefix: string) => `${prefix}-${moment().unix()}-${Math.random()}`;

const ReferralSubmission: React.FC = () => {
  const dispatch = useAppDispatch();
  const [referralContext, setReferralContext] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    ohip_number: '',
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fillingTemplateId, setFillingTemplateId] = useState<{
    id: string;
    readOnly: boolean;
  } | null>(null);
  const [previewingTemplate, setPreviewingTemplate] = useState<Attachment | null>(null);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [fullTemplate, setFullTemplate] = useState<DocumentTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const templateController = useRef<DocumentTemplateController>({});

  function isServicesTemplate(template: DocumentTemplate) {
    return template.description?.toLowerCase().includes('services');
  }

  // Load referral templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      // Anonymous page: the tenant_id API header comes from portal-data, whose
      // warmup may still be in flight on a cold cache when this mounts.
      const result = await dispatch(api.Templates.getReferralSubmissionTemplates(), {
        errorTitle: 'Load Referral Forms',
      });

      if (result) {
        setTemplates([
          ...result.filter((template) => !isServicesTemplate(template)),
          ...result.filter(isServicesTemplate),
        ]);
      }
      setLoadingTemplates(false);
    };

    loadTemplates();
  }, [dispatch]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const currentFileCount = attachments.filter((att) => att.type === 'file').length;
      const maxFiles = 5;

      // Check if adding these files would exceed the limit
      if (currentFileCount + newFiles.length > maxFiles) {
        MiscUtils.showToastrError(
          'Upload Limit',
          `You can only upload up to ${maxFiles} files. Please remove some files first.`,
        );
        return;
      }

      const newAttachments: Attachment[] = newFiles.map((file) => ({
        id: generateAttachmentId('file'),
        type: 'file',
        name: file.name,
        size: file.size,
        file,
      }));
      setAttachments([...attachments, ...newAttachments]);
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachments(attachments.filter((att) => att.id !== attachmentId));
  };

  const handleViewAttachment = async (attachment: Attachment) => {
    if (attachment.type === 'file' && attachment.file) {
      // Open file in new tab
      const url = URL.createObjectURL(attachment.file);
      window.open(url, '_blank');
    } else if (attachment.type === 'template' && attachment.template) {
      // Load full template and show preview modal
      const template = templates.find((t) => t.id === attachment.template!.template_id);
      if (template) {
        setFullTemplate(template);
        setPreviewingTemplate(attachment);
      } else {
        MiscUtils.showToastrError('Preview Error', 'Unable to load template for preview');
      }
    }
  };

  const handleFillTemplate = async (template: DocumentTemplate, readOnly: boolean) => {
    setFillingTemplateId({ id: template.id!, readOnly });
    setFullTemplate(template);
  };

  const handleDownloadTemplate = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template || !template.pdf) return;

    try {
      // Fetch the PDF as a blob
      const response = await fetch(template.pdf);
      const blob = await response.blob();

      // Create object URL and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.description}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
      MiscUtils.showToastrError(
        'Download Failed',
        'Failed to download template. Please try again.',
      );
    }
  };

  const handleSaveFilledTemplate = async () => {
    const filledData = templateController.current?.getTemplate
      ? await templateController.current.getTemplate()
      : undefined;

    if (!filledData || !fillingTemplateId) {
      return;
    }

    const template = templates.find((t) => t.id === fillingTemplateId.id);
    if (!template) return;

    const newAttachment: Attachment = {
      id: generateAttachmentId('template'),
      type: 'template',
      name: template.description!,
      template: {
        template_id: fillingTemplateId.id,
        template_name: template.description!,
        html: filledData.html,
        metadata: filledData.template_metadata || '{}',
      },
    };

    setAttachments([...attachments, newAttachment]);
    handleCancelFillTemplate();
  };

  const handleCancelFillTemplate = () => {
    setFillingTemplateId(null);
    setFullTemplate(null);
  };

  const handleClosePreview = () => {
    setPreviewingTemplate(null);
    setFullTemplate(null);
  };

  const handleSaveEditedTemplate = async () => {
    if (!previewingTemplate || !templateController.current?.getTemplate) {
      return;
    }

    const filledData = await templateController.current.getTemplate();
    if (!filledData) {
      return;
    }

    // Update the attachment with new data
    const updatedAttachments = attachments.map((att) => {
      if (att.id === previewingTemplate.id) {
        return {
          ...att,
          template: {
            ...att.template!,
            html: filledData.html,
            metadata: filledData.template_metadata || '{}',
          },
        };
      }
      return att;
    });

    setAttachments(updatedAttachments);
    handleClosePreview();
  };

  // Helper function to check if a template has already been filled
  const isTemplateFilled = (templateId: string) => {
    return attachments.some(
      (att) => att.type === 'template' && att.template?.template_id === templateId,
    );
  };

  const validateForm = () => {
    const validations = [
      { condition: !referralContext.trim(), message: 'Please provide referral context' },
      { condition: !submitterEmail.trim(), message: 'Your email address is required' },
      {
        condition: !ValidatorUtils.checkIsEmail(submitterEmail.trim()),
        message: 'Please enter a valid email address for your email',
      },
      { condition: !patientInfo.first_name.trim(), message: 'Patient first name is required' },
      { condition: !patientInfo.last_name.trim(), message: 'Patient last name is required' },
      { condition: !patientInfo.email.trim(), message: 'Patient email is required' },
      {
        condition: !ValidatorUtils.checkIsEmail(patientInfo.email.trim()),
        message: 'Please enter a valid patient email address',
      },
    ];

    for (const validation of validations) {
      if (validation.condition) {
        MiscUtils.showToastrError('Validation Error', validation.message);
        return false;
      }
    }
    return true;
  };

  const clearForm = () => {
    setPatientInfo({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      ohip_number: '',
    });
    setReferralContext('');
    setSubmitterEmail('');
    setAttachments([]);
  };

  const handleSubmit = async () => {
    // Validation
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Prepare attachments metadata
      const attachmentsMetadata = attachments.map((att) => {
        if (att.type === 'file') {
          return {
            type: 'file',
            filename: att.name,
            size: att.size,
          };
        } else {
          return {
            type: 'template',
            template_id: att.template!.template_id,
            template_name: att.template!.template_name,
            html: att.template!.html,
            metadata: att.template!.metadata,
          };
        }
      });

      // Prepare file attachments (up to 5 files)
      const fileAttachments = attachments.filter((att) => att.type === 'file');
      const requestParams: SubmitExternalReferralRequest = {
        patientFirstName: patientInfo.first_name,
        patientLastName: patientInfo.last_name,
        patientEmail: patientInfo.email,
        patientPhone: patientInfo.phone || undefined,
        patientOhip: patientInfo.ohip_number || undefined,
        referralContext,
        submitterEmail,
        attachmentsMetadata: JSON.stringify(attachmentsMetadata),
      };

      const fileKeys = ['file0', 'file1', 'file2', 'file3', 'file4'] as const;
      fileKeys.forEach((key, index) => {
        if (fileAttachments[index] && fileAttachments[index].file) {
          requestParams[key] = fileAttachments[index].file;
        }
      });

      // Submit to API
      const result = await dispatch(api.Referrals.submitExternalReferral(requestParams), {
        title: 'Referral Submission',
        successText: 'Referral submitted successfully!',
      });

      if (result) {
        clearForm();
      }
    } catch (error) {
      console.error('Failed to submit referral:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    referralContext.trim() &&
    submitterEmail.trim() &&
    patientInfo.first_name.trim() &&
    patientInfo.last_name.trim() &&
    patientInfo.email.trim();

  const fakePatient: Patient = {
    id: '',
    first_name: patientInfo.first_name,
    last_name: patientInfo.last_name,
    email: patientInfo.email,
    phone: patientInfo.phone,
    health_card: patientInfo.ohip_number
      ? {
          province: 'Ontario',
          health_number: patientInfo.ohip_number,
        }
      : undefined,
    notification_settings: {},
  };

  // Empty physician object to prevent dummy data
  const emptyPhysician: Physician = {
    id: '',
    first_name: '',
    last_name: '',
    billing_number: '',
    title: '',
    college_registration: '',
  };

  return (
    <div className="referral-submission-page">
      {/* Page Header */}
      <div className="page-header">
        <a href="https://careand.ca" target="_blank" rel="noopener noreferrer">
          <img src={logo} alt="Care& Family Health" className="logo" />
        </a>
      </div>

      <div className="content">
        <h1 className="page-title">Submit Referral</h1>

        {/* Section 1: Patient Information */}
        <section className="patient-info">
          <h2>Patient Information</h2>
          <div className="patient-info-grid">
            <DefaultTextField
              key="patient-first-name"
              label="Patient First Name *"
              style={{ width: '100%' }}
              value={patientInfo.first_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPatientInfo({ ...patientInfo, first_name: e.target.value })
              }
              placeholder="First name"
            />
            <DefaultTextField
              key="patient-last-name"
              label="Patient Last Name *"
              style={{ width: '100%' }}
              value={patientInfo.last_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPatientInfo({ ...patientInfo, last_name: e.target.value })
              }
              placeholder="Last name"
            />
            <DefaultTextField
              key="patient-email"
              label="Patient Email *"
              style={{ width: '100%' }}
              value={patientInfo.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPatientInfo({ ...patientInfo, email: e.target.value })
              }
              placeholder="email@example.com"
            />
            <DefaultTextField
              key="patient-phone"
              label="Patient Phone (Optional)"
              style={{ width: '100%' }}
              value={patientInfo.phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPatientInfo({ ...patientInfo, phone: e.target.value })
              }
              placeholder="(555) 123-4567"
            />
            <DefaultTextField
              key="patient-ohip"
              label="OHIP Number (Optional)"
              style={{ width: '100%' }}
              value={patientInfo.ohip_number}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPatientInfo({ ...patientInfo, ohip_number: e.target.value })
              }
              placeholder="1234-567-890"
            />
          </div>
        </section>

        {/* Section 2: Referral Context */}
        <section className="referral-context">
          <h2>Referral Information</h2>
          <div className="field-group">
            <DefaultTextField
              key="submitter-email"
              label="Your Email Address *"
              style={{ width: '100%' }}
              value={submitterEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSubmitterEmail(e.target.value)
              }
              placeholder="your.email@example.com"
            />
          </div>
          <div className="field-group">
            <label>Clinical Notes / Reason for Referral *</label>
            <textarea
              value={referralContext}
              onChange={(e) => setReferralContext(e.target.value)}
              placeholder="Please describe the reason for referral, relevant clinical history, current medications, and any specific concerns or questions..."
              rows={8}
              required
            />
          </div>
        </section>

        {/* Section 3: Documents & Forms */}
        <section className="documents-and-forms">
          <h2>Documents & Forms</h2>
          <p className="help-text">
            Fill out referral forms digitally, download blank PDFs, or upload your own documents.
          </p>

          {/* Referral Forms */}
          <div className="referral-forms-section">
            <h4>Available Referral Forms</h4>
            {loadingTemplates ? (
              <p className="help-text">Loading forms...</p>
            ) : templates.length === 0 ? (
              <p className="help-text">No referral forms available at this time.</p>
            ) : (
              <div className="forms-list">
                {templates.map((template) => {
                  const alreadyFilled = isTemplateFilled(template.id!);
                  const isServices =
                    template.description?.toLowerCase().includes('services') || false;
                  return (
                    <div key={template.id} className="form-item">
                      <Icon src="file-lines" width="20px" className="blue" />
                      <span className="form-name">{template.description}</span>
                      <div className="form-actions">
                        <DefaultButton
                          name={isServices ? 'View' : 'Fill'}
                          icon={isServices ? 'eye' : 'pen'}
                          className="blue small"
                          onClick={() => handleFillTemplate(template, isServices)}
                          disabled={alreadyFilled}
                        />
                        <DefaultButton
                          name="Download"
                          icon="download"
                          className="light"
                          onClick={() => handleDownloadTemplate(template.id!)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="section-divider">
            <span>OR Upload Files</span>
          </div>

          {/* File Upload */}
          <div className="file-upload-section">
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload-input"
            />
            <label htmlFor="file-upload-input">
              <DefaultButton
                name="Upload Files"
                icon="paperclip"
                onClick={() => document.getElementById('file-upload-input')?.click()}
              />
            </label>
            <p className="help-text" style={{ marginTop: '8px', marginBottom: '0' }}>
              Maximum 5 files. Accepted: PDF, JPG, PNG
            </p>
          </div>

          {/* Attachments List */}
          {attachments.length > 0 && (
            <div className="attachments-list">
              <h4>Attached ({attachments.length})</h4>
              {attachments.map((attachment) => (
                <div key={attachment.id} className="attachment-item">
                  <Icon src={attachment.type === 'file' ? 'file' : 'file-lines'} width="20px" />
                  <div className="attachment-info">
                    <span className="attachment-name">{attachment.name}</span>
                    {attachment.size && (
                      <span className="attachment-size">
                        ({(attachment.size / 1024).toFixed(1)} KB)
                      </span>
                    )}
                    {attachment.type === 'template' && (
                      <span className="attachment-badge">Filled Form</span>
                    )}
                  </div>
                  <div className="attachment-actions">
                    <DefaultButton
                      name={attachment.type === 'template' ? 'Edit' : 'View'}
                      icon={attachment.type === 'template' ? 'pen' : 'eye'}
                      className="light small"
                      onClick={() => handleViewAttachment(attachment)}
                    />
                    <DefaultButton
                      name="Delete"
                      icon="trash"
                      className="light small red"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 5: Submit */}
        <div className="submit-section">
          <DefaultButton
            name="Submit Referral"
            className="blue large"
            icon="paper-plane"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
          />
          {!canSubmit && !submitting && (
            <p className="warning-text">
              <Icon src="circle-exclamation" width="16px" className="orange" />
              Please provide your email, referral context, patient first name, patient last name,
              and patient email
            </p>
          )}
        </div>
      </div>

      {/* Template Filling Modal */}
      {fillingTemplateId && (
        <div className="template-fill-modal">
          <div className="modal-overlay" onClick={handleCancelFillTemplate} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                Fill Form: {templates.find((t) => t.id === fillingTemplateId.id)?.description}
              </h3>
              <Icon
                src="times"
                width="20px"
                className="close-icon clickable"
                onClick={handleCancelFillTemplate}
              />
            </div>
            <div className="modal-body">
              {fullTemplate ? (
                <DocumentTemplateComponent
                  key={fullTemplate.id}
                  template={fullTemplate}
                  onClose={handleCancelFillTemplate}
                  patient={fakePatient}
                  physician={emptyPhysician}
                  document={undefined}
                  hideDocumentPanel={true}
                  templateOnly={true}
                  noZoom={true}
                  hideAddPage={true}
                  editMode={false}
                  defaultSavedDocumentCategory={undefined}
                  onSaveCompleted={() => {}}
                  prefilledValues={undefined}
                  documentTemplateController={templateController.current}
                />
              ) : (
                <p>Failed to load template</p>
              )}
            </div>
            {!fillingTemplateId.readOnly && (
              <div className="modal-footer">
                <DefaultButton name="Cancel" className="light" onClick={handleCancelFillTemplate} />
                <DefaultButton
                  name="Save & Attach"
                  className="blue"
                  icon="check"
                  onClick={handleSaveFilledTemplate}
                  disabled={!fullTemplate}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Edit Modal */}
      {previewingTemplate && previewingTemplate.template && (
        <div className="template-fill-modal">
          <div className="modal-overlay" onClick={handleClosePreview} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit: {previewingTemplate.name}</h3>
              <Icon
                src="times"
                width="20px"
                className="close-icon clickable"
                onClick={handleClosePreview}
              />
            </div>
            <div className="modal-body">
              {fullTemplate ? (
                <DocumentTemplateComponent
                  key={`edit-${fullTemplate.id}`}
                  template={fullTemplate}
                  onClose={handleClosePreview}
                  patient={fakePatient}
                  physician={emptyPhysician}
                  document={undefined}
                  hideDocumentPanel={true}
                  templateOnly={true}
                  noZoom={true}
                  hideAddPage={true}
                  editMode={false}
                  defaultSavedDocumentCategory={undefined}
                  onSaveCompleted={() => {}}
                  prefilledValues={JSON.parse(previewingTemplate.template.metadata)}
                  documentTemplateController={templateController.current}
                />
              ) : (
                <p>Failed to load template</p>
              )}
            </div>
            <div className="modal-footer">
              <DefaultButton name="Cancel" className="light" onClick={handleClosePreview} />
              <DefaultButton
                name="Save Changes"
                className="blue"
                icon="check"
                onClick={handleSaveEditedTemplate}
                disabled={!fullTemplate}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralSubmission;
